/**
 * «Чек-листы» — день по участкам и ответы одного прохождения.
 *
 * Только просмотр: чек-листы проходят все и только в Telegram (§3 ТЗ
 * портала, решение владельца 16.09.2026). Управляющий (право «Чек-листы ·
 * править») видит смену целиком, остальные — чек-листы своей должности.
 * Прошлые дни — выбором даты; дата живёт в адресе.
 */

import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { Icon } from '../../icons';
import { Link, navigate, useRoute } from '../../router';
import { Notice, Pill, isoDate } from '../../ui';
import { LIVE, shiftApi } from './api';
import type { ChecklistDay, RunDetail, RunItemRow, RunRow, ShiftAccess } from './api';
import { tabPath } from './paths';
import { Photos, RunStatus, hhmm, plural, shiftDay } from './shared';

export function Checklists({ runId, access }: { runId: number | null; access: ShiftAccess }) {
  return runId !== null ? <RunView id={runId} /> : <DayView access={access} />;
}

function DayView({ access }: { access: ShiftAccess }) {
  const route = useRoute();
  const date = route.params.get('date') ?? undefined;
  const query = useQuery<ChecklistDay, ApiError>({
    queryKey: ['shift', 'checklists', date ?? 'today'],
    queryFn: () => shiftApi.checklists(date),
    ...LIVE,
  });
  const go = (value: string | undefined) =>
    navigate(value && value !== access.today ? `/shift/checklists?date=${value}` : '/shift/checklists', {
      replace: true,
    });

  const current = query.data?.date ?? date ?? access.today;
  return (
    <div className="shift-stack">
      <p className="shift-hint">
        <Icon name="shift" size={16} /> Чек-листы заполняют в операционном боте. Здесь — что
        сделано, кем и с какими ответами.
      </p>
      <div className="shift-dayline">
        <button
          type="button"
          className="button button--small"
          aria-label="Предыдущий день"
          onClick={() => go(shiftDay(current, -1))}
        >
          <Icon name="back" size={14} />
        </button>
        <input
          className="input shift-date"
          type="date"
          aria-label="День"
          value={current}
          max={access.today}
          onChange={(event) => go(event.target.value || undefined)}
        />
        <button
          type="button"
          className="button button--small shift-next"
          aria-label="Следующий день"
          disabled={current >= access.today}
          onClick={() => go(shiftDay(current, 1))}
        >
          <Icon name="back" size={14} />
        </button>
        {current !== access.today && (
          <button type="button" className="button button--small" onClick={() => go(undefined)}>
            Сегодня
          </button>
        )}
      </div>

      {query.isPending ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <DayBody day={query.data} />
      )}
    </div>
  );
}

function DayBody({ day }: { day: ChecklistDay }) {
  const { totals } = day;
  if (totals.planned === 0) {
    return (
      <Empty
        title={`На ${isoDate(day.date)} чек-листов нет`}
        text={
          day.whole_shift
            ? 'Расписание ничего не выдало на этот день — проверьте шаблоны.'
            : 'Для вашей должности на этот день чек-листов не было.'
        }
      />
    );
  }
  return (
    <>
      <div className="shift-tiles" aria-label="Итог дня">
        <Tile label={day.whole_shift ? 'Вся смена' : 'Мои чек-листы'} value={`${totals.planned}`} />
        <Tile label="В окно" value={`${totals.done}`} />
        <Tile label="С опозданием" value={`${totals.done_late}`} />
        <Tile label="Просрочено" value={`${totals.missed}`} tone={totals.missed ? 'red' : undefined} />
        <Tile label="Ещё в работе" value={`${totals.open}`} />
      </div>
      {day.areas.map((group) => (
        <section className="shift-card" key={group.area_id ?? 'none'}>
          <header className="shift-card__head">
            <h2 className="shift-card__title">{group.title}</h2>
            <span className="faint">
              {group.runs.length} {plural(group.runs.length, 'чек-лист', 'чек-листа', 'чек-листов')}
            </span>
          </header>
          <ul className="shift-list">
            {group.runs.map((run) => (
              <RunLine key={run.id} run={run} showPosition={day.whole_shift} />
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

export function Tile({ label, value, tone }: { label: string; value: string; tone?: 'red' }) {
  return (
    <div className={`shift-tile${tone ? ` shift-tile--${tone}` : ''}`}>
      <span className="shift-tile__label">{label}</span>
      <span className="shift-tile__value">{value}</span>
    </div>
  );
}

export function RunLine({ run, showPosition }: { run: RunRow; showPosition: boolean }) {
  const who =
    run.performer ?? (run.status === 'missed' || run.status === 'pending' ? 'никто не взял' : '');
  return (
    <li>
      <Link to={tabPath('checklists', run.id)} className="shift-row">
        <span className="shift-row__main">
          <span className="shift-row__title">{run.title}</span>
          <span className="faint">
            {hhmm(run.window_from)}–{hhmm(run.window_to)}
            {showPosition && run.position ? ` · ${run.position}` : ''}
            {who ? ` · ${who}` : ''}
          </span>
        </span>
        <span className="shift-row__side">
          <span className="faint num">
            {run.answered} из {run.total}
          </span>
          <RunStatus status={run.status} title={run.status_title} />
        </span>
      </Link>
    </li>
  );
}

function RunView({ id }: { id: number }) {
  const query = useQuery<RunDetail, ApiError>({
    queryKey: ['shift', 'run', id],
    queryFn: () => shiftApi.run(id),
    ...LIVE,
  });

  const back = (
    <button
      type="button"
      className="shift-back"
      onClick={() => (window.history.length > 1 ? window.history.back() : navigate('/shift/checklists'))}
    >
      <Icon name="back" size={16} /> К чек-листам дня
    </button>
  );
  if (query.isPending) return <Loading />;
  if (query.error) {
    return (
      <div className="shift-stack">
        {back}
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }
  const { run, items, note } = query.data;
  return (
    <div className="shift-stack">
      {back}
      <article className="shift-card" aria-label="Прохождение">
        <header className="shift-card__head">
          <div>
            <h2 className="shift-card__title">{run.title}</h2>
            <p className="faint">
              {isoDate(run.date)} · окно {hhmm(run.window_from)}–{hhmm(run.window_to)}
              {run.area ? ` · ${run.area}` : ''}
              {run.position ? ` · ${run.position}` : ''}
            </p>
          </div>
          <RunStatus status={run.status} title={run.status_title} />
        </header>
        <p className="faint">
          {run.performer ? `Заполнял: ${run.performer}` : 'Никто не взял'}
          {run.started_at ? ` · начал ${hhmm(run.started_at)}` : ''}
          {run.finished_at ? ` · закончил ${hhmm(run.finished_at)}` : ''} · отвечено {run.answered}{' '}
          из {run.total}
        </p>
        <Notice>{note}</Notice>
        <ol className="shift-items">
          {items.map((item) => (
            <ItemLine key={item.id} item={item} />
          ))}
        </ol>
      </article>
    </div>
  );
}

const STATE_COLOURS: Record<string, string> = { done: 'green', cant: 'red', na: 'grey' };

function ItemLine({ item }: { item: RunItemRow }) {
  return (
    <li className={`shift-item${item.problem ? ' shift-item--problem' : ''}`}>
      <div className="shift-item__head">
        <span className="shift-item__title">
          {item.order_no}. {item.title}
          {item.is_critical && <span className="shift-item__critical"> · критичный</span>}
        </span>
        {item.state ? (
          <Pill colour={STATE_COLOURS[item.state]}>{item.state_title}</Pill>
        ) : (
          <span className="faint">без ответа</span>
        )}
      </div>
      {(item.value || item.allowed) && (
        <p className="shift-item__value">
          {item.value && <strong>{item.value}</strong>}
          {item.allowed && <span className="faint"> допустимо {item.allowed}</span>}
        </p>
      )}
      {item.comment && <p className="shift-item__comment">«{item.comment}»</p>}
      {item.problem && <p className="shift-item__problem">{item.problem}</p>}
      <Photos photos={item.photos} alt={item.title} />
      {item.tasks.length > 0 && (
        <ul className="shift-item__tasks">
          {item.tasks.map((task) => (
            <li key={task.id}>
              <Link to={tabPath('tasks', task.id)}>
                Задача «{task.title}» — {task.status_title}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {item.answered_at && <p className="faint shift-item__time">ответ в {hhmm(item.answered_at)}</p>}
    </li>
  );
}
